import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "../../lib/api";
import { SALayout } from "../../components/superadmin/SALayout";
import { formatDate } from "../../lib/format";
import { useSAAuth } from "../../contexts/SAAuthContext";
import { Plus, X, ArrowRight } from "lucide-react";

const STAGES = ["Lead", "Contacted", "Demo Scheduled", "Trial Started", "Won", "Lost"];
const TYPE_OPTS = ["Bar", "Restaurant", "Lounge", "Club", "Cafe", "Hotel"];
const SOURCE_OPTS = ["Inbound", "Referral", "Outbound", "Event", "Other"];

export default function SAPipeline() {
  const { user } = useSAAuth();
  const isAdmin = user?.role === "Admin";
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // lead detail
  const [showAdd, setShowAdd] = useState(false);

  const reload = async () => {
    const { data } = await api.get("/admin/pipeline");
    setLeads(data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const grouped = STAGES.reduce((acc, s) => { acc[s] = leads.filter((l) => l.stage === s); return acc; }, {});

  const moveStage = async (lead, next) => {
    if (!isAdmin) return;
    try {
      await api.put(`/admin/pipeline/${lead.id}`, { stage: next });
      await reload();
      if (open && open.id === lead.id) {
        const fresh = (await api.get("/admin/pipeline")).data.find((l) => l.id === lead.id);
        if (fresh) setOpen(fresh);
      }
      toast.success(`Moved to ${next}`);
    } catch (e) { toast.error(formatError(e)); }
  };

  const convert = async (lead) => {
    if (!isAdmin) return;
    if (!window.confirm(`Convert ${lead.venue_name} to a live Trial venue? This creates a venue record + staff code.`)) return;
    try {
      const { data } = await api.post(`/admin/pipeline/${lead.id}/convert-to-trial`);
      toast.success(`Trial created · Venue Code ${data.venue_code}`);
      await reload();
      setOpen(null);
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <SALayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">Onboarding Pipeline</h1>
          <p className="text-sm text-[#A0A0A0] mt-1">Track every venue from Lead to Live Trial.</p>
        </div>
        {isAdmin && (
          <button
            data-testid="sa-pipeline-add-lead"
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 bg-[#9D00FF] hover:bg-[#8C00E6] text-white text-sm font-semibold rounded-lg px-4 py-2"
          >
            <Plus size={14} /> Add Lead
          </button>
        )}
      </div>

      <div className="overflow-x-auto pb-4" data-testid="sa-pipeline-kanban">
        <div className="flex gap-4 min-w-[1200px]">
          {STAGES.map((stage) => (
            <div key={stage} className="flex-1 min-w-[220px] bg-[#1A1A1A] border border-white/10 rounded-xl p-3">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-bold uppercase tracking-wider text-white/90">{stage}</div>
                <div className="text-[10px] text-[#A0A0A0] bg-white/5 rounded-full px-2 py-0.5">{grouped[stage].length}</div>
              </div>
              <div className="flex flex-col gap-2">
                {loading && <div className="text-xs text-[#A0A0A0]">Loading…</div>}
                {!loading && grouped[stage].length === 0 && (
                  <div className="text-xs text-[#A0A0A0] py-4 text-center">Empty</div>
                )}
                {grouped[stage].map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setOpen(l)}
                    data-testid={`sa-pipeline-card-${l.id}`}
                    className="text-left bg-[#0A0A0A] border border-white/10 hover:border-[#9D00FF]/50 rounded-lg p-3 transition"
                  >
                    <div className="font-semibold text-white text-sm">{l.venue_name}</div>
                    <div className="text-[11px] text-[#A0A0A0] mt-0.5">{l.city} · {l.type}</div>
                    <div className="text-[11px] text-[#A0A0A0] mt-1">{l.owner_name} · {l.phone}</div>
                    {l.follow_up_date && <div className="text-[10px] text-[#F59E0B] mt-1">Follow-up {formatDate(l.follow_up_date)}</div>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {open && (
        <LeadDrawer
          lead={open}
          isAdmin={isAdmin}
          onClose={() => setOpen(null)}
          onMove={moveStage}
          onConvert={convert}
          onRefresh={async () => {
            const { data } = await api.get("/admin/pipeline");
            setLeads(data);
            const fresh = data.find((l) => l.id === open.id);
            if (fresh) setOpen(fresh);
          }}
        />
      )}
      {showAdd && (
        <AddLeadDialog onClose={() => setShowAdd(false)} onCreated={async () => { setShowAdd(false); await reload(); }} />
      )}
    </SALayout>
  );
}

function LeadDrawer({ lead, isAdmin, onClose, onMove, onConvert, onRefresh }) {
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState(lead.follow_up_date ? lead.follow_up_date.slice(0, 10) : "");

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/admin/pipeline/${lead.id}/notes`, { text: note.trim() });
      setNote("");
      await onRefresh();
      toast.success("Note added");
    } catch (e) { toast.error(formatError(e)); }
  };
  const saveFollowUp = async () => {
    try {
      await api.put(`/admin/pipeline/${lead.id}`, { follow_up_date: followUp || null });
      await onRefresh();
      toast.success("Follow-up saved");
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex" onClick={onClose}>
      <div className="ml-auto h-full w-full max-w-xl bg-[#0A0A0A] border-l border-white/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0A0A0A] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-black text-xl">{lead.venue_name}</div>
            <div className="text-xs text-[#A0A0A0]">{lead.city} · {lead.type} · {lead.source}</div>
          </div>
          <button onClick={onClose} className="p-2 border border-white/10 rounded-lg" data-testid="sa-lead-drawer-close"><X size={16} /></button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <KV label="Owner" value={lead.owner_name} />
            <KV label="Phone" value={lead.phone} />
            <KV label="Email" value={lead.email || "—"} />
            <KV label="Stage" value={lead.stage} />
            {lead.venue_code && <KV label="Venue Code" value={lead.venue_code} />}
          </div>

          {isAdmin && (
            <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-[#A0A0A0] mb-2">Move Stage</div>
              <div className="flex flex-wrap gap-2">
                {STAGES.map((s) => (
                  <button
                    key={s}
                    onClick={() => onMove(lead, s)}
                    data-testid={`sa-lead-move-${s.replace(/\W/g, "-").toLowerCase()}`}
                    className={`text-xs font-semibold rounded-full px-3 py-1 border ${
                      lead.stage === s ? "bg-[#9D00FF] border-[#9D00FF] text-white" : "border-white/10 text-white/70 hover:border-white/30"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {!lead.venue_id && (
                <button
                  onClick={() => onConvert(lead)}
                  data-testid="sa-lead-convert"
                  className="mt-3 flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg px-3 py-2"
                >
                  <ArrowRight size={14} /> Convert to Trial venue
                </button>
              )}
            </div>
          )}

          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4">
            <div className="text-xs uppercase tracking-wider text-[#A0A0A0] mb-2">Follow-up Date</div>
            <div className="flex gap-2">
              <input
                type="date"
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
                data-testid="sa-lead-followup"
                className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              />
              <button
                onClick={saveFollowUp}
                className="bg-[#9D00FF] hover:bg-[#8C00E6] rounded-lg px-4 py-2 text-xs font-semibold text-white"
              >Save</button>
            </div>
          </div>

          <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4">
            <div className="text-xs uppercase tracking-wider text-[#A0A0A0] mb-2">Activity</div>
            <div className="flex gap-2 mb-3">
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note…"
                data-testid="sa-lead-note-input"
                className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
              />
              <button
                onClick={addNote}
                data-testid="sa-lead-note-save"
                className="bg-[#9D00FF] hover:bg-[#8C00E6] rounded-lg px-4 py-2 text-xs font-semibold text-white"
              >Add</button>
            </div>
            <div className="flex flex-col divide-y divide-white/5 max-h-80 overflow-y-auto">
              {(lead.notes_log || []).length === 0 && <div className="py-3 text-[#A0A0A0] text-sm">No activity.</div>}
              {(lead.notes_log || []).slice().reverse().map((n, i) => (
                <div key={i} className="py-3">
                  <div className="text-sm text-white">{n.text}</div>
                  <div className="text-[11px] text-[#A0A0A0] mt-1">{n.created_by_name || "System"} · {formatDate(n.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddLeadDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    venue_name: "", owner_name: "", phone: "", email: "",
    city: "", type: "Bar", source: "Inbound", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/pipeline", form);
      toast.success("Lead added");
      onCreated();
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Add Lead</h3>
          <button type="button" onClick={onClose} className="p-2 border border-white/10 rounded-lg"><X size={14} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <In label="Venue Name" testid="lead-venue-name" value={form.venue_name} onChange={(v) => setForm({ ...form, venue_name: v })} required />
          <In label="City" testid="lead-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} required />
          <In label="Owner Name" testid="lead-owner" value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} required />
          <In label="Phone" testid="lead-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
          <In label="Email" testid="lead-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <Sel label="Type" testid="lead-type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} opts={TYPE_OPTS} />
          <Sel label="Source" testid="lead-source" value={form.source} onChange={(v) => setForm({ ...form, source: v })} opts={SOURCE_OPTS} />
        </div>
        <label className="block mt-3">
          <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">Notes</div>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            data-testid="lead-notes"
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          />
        </label>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={onClose} className="border border-white/10 rounded-lg px-4 py-2 text-sm">Cancel</button>
          <button
            type="submit"
            disabled={loading}
            data-testid="sa-lead-create-submit"
            className="bg-[#9D00FF] hover:bg-[#8C00E6] disabled:opacity-60 text-white text-sm font-semibold rounded-lg px-5 py-2"
          >
            {loading ? "Saving…" : "Add Lead"}
          </button>
        </div>
      </form>
    </div>
  );
}

function In({ label, value, onChange, testid, type = "text", required }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">{label}</div>
      <input
        data-testid={testid} type={type} required={required} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#9D00FF]"
      />
    </label>
  );
}
function Sel({ label, value, onChange, opts, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1">{label}</div>
      <select
        data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
      >
        {opts.map((o) => <option key={o}>{o}</option>)}
      </select>
    </label>
  );
}
function KV({ label, value }) {
  return (
    <div className="bg-[#1A1A1A] border border-white/10 rounded-lg px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">{label}</div>
      <div className="text-sm text-white">{value}</div>
    </div>
  );
}
